import NextLink from 'next/link'
import NProgress from 'nprogress'
import {
    FocusEvent,
    KeyboardEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import './fix-tree-walker'

import { ChevronDown, ChevronRight, Settings2Icon, XIcon } from 'lucide-react'
import {
    CreateHandler,
    DeleteHandler,
    MoveHandler,
    NodeApi,
    NodeRendererProps,
    RenameHandler,
    SimpleTree,
    Tree,
} from 'react-arborist'

import classNames from 'classnames'
import { useRouter } from 'next/router'
import { PagesTree } from 'website/src/lib/editor-types'
import {
    cn,
    editorBasePath,
    fixNodePageSlug,
    fixPagesTreeSlugsBasedOnPosition,
    formatPagesTree,
    getCurrentPageSlug,
    isTruthy,
    pagesTreeBfs,
    removeQueryStr,
    withEditorBasePath,
} from 'website/src/lib/editor-utils'
import {
    DisableForAnons,
    mergeRefs,
    useBetterStore,
    useIsMounted,
    useRootProps,
} from 'website/src/lib/hooks'

import { Link, Spinner, useThrowingFn } from 'beskar/landing'
import toast from 'react-hot-toast'

import { useStore } from '@nanostores/react'
import { atoms } from 'website/src/lib/atoms'
import {
    getSearchData,
    switchOrg,
    updateTree,
} from 'website/src/pages/api/functions'
import TeamSwitcher from '../dashboard/team-switcher'

import {
    Button,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownTrigger,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Tooltip,
    useDisclosure,
} from '@nextui-org/react'
import { useSupabaseClient } from '@supabase/auth-helpers-react'
import path from 'path'
import { createPortal } from 'react-dom'
import { v4 } from 'uuid'
import { FillFlexParent } from 'website/src/components/FillFlexParent'
import { Search } from 'website/src/components/editor/Search'
import { githubProjectTypes } from 'website/src/lib/constants'
import { DarkModeSwitch } from './DarkModeSwitch'
import { PublishButton } from 'website/src/components/editor/PublishButton'

function useNewPageOrFolder({
    node,
    isFolder = false,
}: {
    node: NodeApi<PagesTree> | null
    isFolder: boolean
}) {
    const { pagesTree } = useRootProps()
    const { isOpen, onOpenChange, onClose, onOpen } = useDisclosure()

    let { handlers, tree } = useSimpleTree((pagesTree as any) || ([] as any))
    let [slug, setSlug] = useState(genSlug())
    const mutateTree = useTreeMutation(tree)
    let parentId = node?.data?.slug || null
    const { fn: createPage, isLoading } = useThrowingFn({
        fn: async function fn() {
            if (!slug) {
                throw new Error(`no slug`)
            }
            if (!isFolder) {
                slug = fixNodePageSlug(slug)
            } else {
                if (!slug.startsWith('/')) {
                    slug = `/${slug}`
                }
            }
            try {
                console.log('creating node', slug)
                let pageId = isFolder ? undefined : v4()
                let children = isFolder ? [] : undefined
                const data: PagesTree<{ id: string }> = {
                    slug,
                    id: slug,
                    pageId,
                    children,
                    placeholder: true,
                }

                tree.create({
                    data,
                    parentId,
                    index: node?.children?.length || pagesTree?.length || 0,
                })
                atoms.pagesTree.set([...tree.data])
                await mutateTree()
            } finally {
                onClose()
            }
        },
    })
    function genSlug() {
        let isPage = !isFolder
        const start = `${node?.data?.slug || ''}/${
            isPage ? 'new-page' : 'new-folder'
        }`
        const duplicates = node
            ? tree
                  .find(node?.data?.slug)
                  ?.children?.filter((x) => x.data.slug.startsWith(start))
            : []

        let slug = `${start}-${(duplicates?.length || 0) + 0}${
            isPage ? '.mdx' : ''
        }`

        return slug
    }

    useEffect(() => {
        if (!isOpen) {
            setSlug(genSlug())
        }
    }, [isOpen])

    return {
        onClick: onOpen,
        isLoading,
        modal: (
            <Modal
                as='form'
                onKeyDown={(e) => {
                    e.stopPropagation()
                }}
                // portalContainer={document.body}
                onSubmit={(e) => {
                    e.preventDefault()
                    createPage()
                }}
                backdrop='blur'
                isOpen={isOpen}
                onOpenChange={onOpenChange}
            >
                <ModalContent>
                    <ModalHeader className='flex flex-col gap-1'></ModalHeader>
                    <ModalBody className='space-y-3'>
                        <div className='flex flex-col gap-3'>
                            <div className=''>Page path</div>
                            <Input
                                onValueChange={setSlug}
                                isRequired
                                autoFocus
                                aria-label='page slug'
                                placeholder={isFolder ? 'folder' : 'page.mdx'}
                                value={slug}
                            />
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color='danger'
                            variant='light'
                            onPress={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            isLoading={isLoading}
                            color='primary'
                            type='submit'
                        >
                            Create {isFolder ? 'folder' : 'page'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        ),
    }
}

export function LeftBar({ className = '' }) {
    const router = useRouter()
    const {
        pagesTree,
        userId,

        siteId,
        branchId,
        sites,
        orgId,
        orgs,
        projectType,
    } = useRootProps()
    const topWebsitePadding = useStore(atoms.topWebsitePadding)
    const supabase = useSupabaseClient()

    const leftBarWidth = useStore(atoms.leftBarWidth)
    return (
        <div
            style={{
                paddingTop: topWebsitePadding,
                width: leftBarWidth,
            }}
            className='pl-6 pb-4 gap-3  h-screen border-r shrink-0 relative flex flex-col'
        >
            <div className='w-full pr-6 relative z-10 flex flex-col gap-3 items-stretch'>
                <TeamSwitcher
                    orgId={orgId}
                    siteId={siteId}
                    onSiteChange={async ({ orgId }) => {
                        if (!userId) {
                            return atoms.showSignupModal.set(true)
                        }
                        // get org first site and redirect to it
                        const { redirect } = await switchOrg({ orgId })
                        if (redirect) {
                            await router.replace(redirect.destination)
                        }
                    }}
                    orgs={orgs?.map((org) => {
                        return {
                            org,
                            sites: sites.filter((x) => x.orgId === org.orgId),
                        }
                    })}
                    className=''
                />

                <div className='flex flex-col gap-1'>
                    <Search className='pl-2 text-default-600 font-medium' />
                    <NextLink
                        className={classNames(
                            'flex flex-col',
                            githubProjectTypes.includes(projectType) &&
                                'pointer-events-none',
                        )}
                        href={`/org/${orgId}/p/${siteId}/settings`}
                    >
                        <DisableForAnons>
                            <Button
                                isDisabled={githubProjectTypes.includes(
                                    projectType,
                                )}
                                // variant='outline'
                                variant='light'
                                aria-label='site settings'
                                // size='sm'
                                startContent={<Settings2Icon className='w-5' />}
                                className={cn(
                                    'truncate pl-2 text-default-600 shrink-0 justify-start font-medium',
                                )}
                            >
                                Settings
                            </Button>
                        </DisableForAnons>
                    </NextLink>
                </div>
                {/* <PublishButton
                    size='sm'
                    className={cn(
                        'truncate border-0 shrink-0 justify-start font-semibold',
                    )}
                /> */}
                {/* <hr className='absolute bottom-0 w-full' /> */}

                <hr className='' />
            </div>
            <FillFlexParent>
                {({ height, width }) => (
                    <TreeSidebarWithoutReset
                        {...{
                            width,
                            height,
                        }}
                    />
                )}
            </FillFlexParent>
            <div className='pr-6 flex flex-col gap-4'>
                <NewPageButton />
                <div className='flex '>
                    <div className='flex flex-col text-sm gap-3'>
                        <Link
                            onClick={async (e) => {
                                e.preventDefault()
                                if (!userId) {
                                    return
                                }
                                await supabase.auth.signOut()
                                window.location.href = '/'
                            }}
                            className='opacity-60'
                        >
                            Sign out &rarr;
                        </Link>
                        <Link href='/blog' className='opacity-60'>
                            Blog
                        </Link>
                    </div>
                    <div className='grow'></div>
                    <div className='h-full flex flex-col'>
                        <div className='grow'></div>
                        <DarkModeSwitch className='' size={24} />
                    </div>
                </div>
            </div>
        </div>
    )
}

function NewPageButton({}) {
    const { projectType, userId } = useRootProps()
    const { modal, onClick: newPage } = useNewPageOrFolder({
        node: null,
        isFolder: false,
    })
    if (githubProjectTypes.includes(projectType)) {
        return null
    }
    return (
        <div className=''>
            <Button
                onClick={() => {
                    if (!userId) {
                        return atoms.showSignupModal.set(true)
                    }
                    newPage()
                }}
                size='sm'
                className='grow w-full text-sm'
            >
                New Page
            </Button>

            {modal}
        </div>
    )
}

export type SimpleTreeData = {
    id: string
    name: string
    children?: SimpleTreeData[]
}

let nextId = 0

function useTreeMutation(
    tree: SimpleTree<PagesTree<{ id: string; name: string }>>,
) {
    const router = useRouter()
    const { siteId, pageId, pageSlug, orgId, branchId } = useRootProps()
    const currentSlug = router.query.siteSlug as string
    // const node = tree.find()

    return async () => {
        NProgress.start()
        atoms.treeIsLoading.set(true)
        try {
            let newTree = fixPagesTreeSlugsBasedOnPosition(tree.data)
            newTree = formatPagesTree(newTree, true)

            console.log('storing new tree', newTree)

            const { deleted, created, moved } = await updateTree({
                pagesTree: newTree,
                siteId,
                branchId,
            })

            let wantedSlug = created?.find((x) => x.pageId)?.slug || ''

            if (moved.some((x) => x.pageId === pageId) && !wantedSlug) {
                wantedSlug = moved?.find((x) => x.pageId)?.slug || ''
            }

            const search = `?increment=${
                Number(router.query.increment || 0) + 1
            }`
            if (wantedSlug) {
                return await router.replace(
                    withEditorBasePath(router.asPath, wantedSlug) + search,
                )
            }

            if (
                deleted.some((x) => x.pageId === pageId) ||
                moved.some((x) => x.pageId === pageId)
            ) {
                // if the current page got deleted
                return await router.replace(
                    editorBasePath(router.asPath) + search,
                )
            }
            await router.replace(removeQueryStr(router.asPath) + search)
        } catch (e: any) {
            const search = `?increment=${
                Number(router.query.increment || 0) + 1
            }`
            await router.replace(removeQueryStr(router.asPath) + search)
            toast.error(e.message)
        } finally {
            NProgress.done()
            atoms.treeIsLoading.set(false)
        }
    }
}

// https://github.com/brimdata/react-arborist/blob/ae57e02abdd04ac9ca3c7f1b98730e2e0ee6f60d/packages/react-arborist/src/hooks/use-simple-tree.ts
// replaces useState with a global store
export function useSimpleTree(initialData: PagesTree<{ id: string }>[]) {
    type T = PagesTree<{ id: string }>
    const { siteId, branchId } = useRootProps()
    const data = useBetterStore({
        atom: atoms.pagesTree,
        default: initialData || [],
    })
    const simpleTree = useMemo(() => {
        return new SimpleTree<any>(data || initialData || [])
    }, [initialData])

    useEffect(() => {
        if (!initialData) {
            return
        }
        atoms.pagesTree.set(initialData)
    }, [initialData])

    const router = useRouter()

    const mutateTree = useTreeMutation(simpleTree)
    const onMove: MoveHandler<T> = (args: {
        dragIds: string[]
        parentId: null | string
        index: number
    }) => {
        for (const id of args.dragIds) {
            simpleTree.move({ id, parentId: args.parentId, index: args.index })
        }
        atoms.pagesTree.set([...simpleTree.data])
        mutateTree()
    }

    const onRename: RenameHandler<T> = ({ name, node, id }) => {
        const slug = path.join(path.dirname(node.data.slug) || '/', name)
        simpleTree.update({ id, changes: { name, slug } as any })
        atoms.pagesTree.set([...simpleTree.data])
        mutateTree()
    }

    const onCreate: CreateHandler<T> = ({ parentId, index, type }) => {
        const data = { id: `simple-tree-id-${nextId++}`, name: '' } as any
        if (type === 'internal') data.children = []
        simpleTree.create({ parentId, index, data })
        atoms.pagesTree.set([...simpleTree.data])
        mutateTree()
        return data
    }

    const onDelete: DeleteHandler<T> = (args: { ids: string[] }) => {
        // console.log('tree onDelete', args)
        args.ids.forEach((id) => simpleTree.drop({ id }))
        atoms.pagesTree.set([...simpleTree.data])
        mutateTree()
    }

    const handlers = { onMove, onRename, onCreate, onDelete }

    return { handlers, tree: simpleTree } as const
}
function fixTree(pagesTree) {
    pagesTreeBfs(pagesTree, (node, parent) => {
        for (let child of node.children || []) {
            child['depth'] = (node['depth'] || 0) + 1
        }

        // https://github.com/brimdata/react-arborist/blob/ae57e02abdd04ac9ca3c7f1b98730e2e0ee6f60d/packages/react-arborist/src/data/simple-tree.ts
        node['id'] = node.slug
        return false
    })
    return pagesTree
}

export function TreeSidebarWithoutReset({ width, height }) {
    const mounted = useIsMounted()

    const { pagesTree: initialPagesTree, pageSlug } = useRootProps()
    const pagesTree = useStore(atoms.pagesTree) || initialPagesTree
    const openState: Record<string, boolean> = {}
    fixTree(pagesTree)

    let { handlers, tree } = useSimpleTree(pagesTree as any)
    const isLoading = useStore(atoms.treeIsLoading)
    let nodes = pagesTreeBfs(pagesTree, (node, parent) => {
        for (let child of node.children || []) {
            child['depth'] = (node['depth'] || 0) + 1
        }

        // https://github.com/brimdata/react-arborist/blob/ae57e02abdd04ac9ca3c7f1b98730e2e0ee6f60d/packages/react-arborist/src/data/simple-tree.ts
        node['id'] = node.slug
        return false
    })
    for (let node of nodes) {
        if (node['depth'] > 2) {
            openState[node.slug] = false
        }
    }

    if (!mounted || !pagesTree?.length) {
        return (
            <div
                className='relative flex flex-col'
                key='tree-container'
                id='tree-container'
            ></div>
        )
    }

    const ml = 10
    return (
        <div
            className='relative '
            style={{
                marginLeft: -ml,
            }}
            key='tree-container'
            id='tree-container'
        >
            {isLoading && (
                <div className='absolute w-full h-full flex flex-col items-center justify-center'>
                    <Spinner className='' />
                </div>
            )}
            <div className='absolute flex flex-col gap-6 pb-4 bottom-0 w-full '>
                {/* <InfoBlock heading={'What is MDX?'}>
                    Files with the <code>.mdx</code> extension are Markdown
                    files that can contain JSX. Some parts are React components
                    that add additional custom blocks to your document.
                </InfoBlock> */}
            </div>
            <Tree
                dndRootElement={
                    typeof window !== 'undefined'
                        ? document.getElementById('tree-container')
                        : null
                }
                // initialData={pagesTree}
                data={pagesTree}
                idAccessor={(x) => x.slug}
                rowClassName='truncate'
                openByDefault={true}
                // width={600}

                // idAccessor={(x) => x.id || x.slug}
                height={height}
                indent={16}
                width={width + ml}
                rowHeight={34}
                // overscanCount={1}
                paddingTop={2}
                paddingBottom={2}
                className={classNames(
                    '!overflow-x-hidden ',
                    isLoading && 'opacity-50 pointer-events-none animate-pulse',
                )}
                selection={pageSlug}
                disableMultiSelection={true}
                initialOpenState={openState}
                {...handlers}
            >
                {Node as any}
            </Tree>
        </div>
    )
}

function RenameForm({
    defaultValue,
    node,
}: {
    defaultValue: string
    node: NodeApi<PagesTree>
}) {
    const inputProps = {
        defaultValue,
        autoFocus: true,
        onBlur: (e: FocusEvent<HTMLInputElement>) => {
            node.submit(e.currentTarget.value)
        },
        onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
            switch (e.key) {
                case 'Enter':
                    node.submit(e.currentTarget.value)
                    break
                case 'Escape':
                    node.reset()
                    break
            }
        },
    }

    return <input type='text' className='focus:outline-none' {...inputProps} />
}

function ContextMenu({
    children,
    node,
}: {
    children: any
    node: NodeApi<PagesTree>
}) {
    const { pagesTree, projectType, userId } = useRootProps()
    let { handlers, tree } = useSimpleTree((pagesTree as any) || ([] as any))

    const { isOpen, onClose, onOpenChange } = useDisclosure() as any
    const { fn: del, isLoading: isDeleting } = useThrowingFn({
        fn: async function fn() {
            try {
                tree!.drop({ id: node.data.slug })
                atoms.pagesTree.set([...tree.data])
                await mutateTree()
            } finally {
                onClose()
            }
        },
    })
    const mutateTree = useTreeMutation(tree)

    const folder = !node.data.pageId

    const {
        modal: pageModal,
        isLoading: creatingPage,
        onClick: newPage,
    } = useNewPageOrFolder({
        node,
        isFolder: false,
    })
    const {
        modal: folderModal,
        isLoading: isCreatingFolder,
        onClick: newFolder,
    } = useNewPageOrFolder({
        node,
        isFolder: true,
    })
    if (!isOpen) {
        return (
            <div
                onContextMenu={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    // document.activeElement?.blur()
                    onOpenChange(true)
                }}
                className=''
            >
                {children}
                {folder && (
                    <>
                        {pageModal}
                        {folderModal}
                    </>
                )}
            </div>
        )
    }

    if (githubProjectTypes.includes(projectType)) {
        return children
    }

    return (
        <>
            <Dropdown
                // key={node.data.pageId || node.data.slug}
                size='sm'
                // disableAnimation
                backdrop='opaque'
                className='dark:bg-gray-700'
                isOpen={isOpen}
                onOpenChange={(isOpen) => !isOpen && onOpenChange(isOpen)}
                closeOnSelect={false}
            >
                <DropdownTrigger
                    onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                    }}
                    className=''
                >
                    {children}
                </DropdownTrigger>
                <DropdownMenu
                    className='text-sm  shadow'
                    aria-label='Static Actions'
                >
                    {folder &&
                        ((
                            <DropdownItem
                                // startContent={isCreatingPage && <Spinner />}

                                className='flex-row gap-2 items-center'
                                onClick={() => {
                                    onClose()
                                    if (!userId) {
                                        return atoms.showSignupModal.set(true)
                                    }
                                    newPage()
                                }}
                            >
                                New Page
                            </DropdownItem>
                        ) as any)}

                    {/* TODO updateTree is removing empty folders, creating a flder is not possible now */}
                    {/* {folder &&
                        ((
                            <DropdownItem
                                // startContent={isCreatingFolder && <Spinner />}
                                className='flex-row gap-2 items-center'
                                aria-label='new folder'
                                onClick={() => {
                                    onClose()
                                    newFolder()
                                }}
                            >
                                New Folder
                            </DropdownItem>
                        ) as any)} */}

                    <DropdownItem
                        // startContent={isRenaming && <Spinner />}
                        aria-label='edit filename'
                        onClick={() => {
                            onClose()
                            if (!userId) {
                                return atoms.showSignupModal.set(true)
                            }
                            node.edit()
                        }}
                    >
                        Rename
                    </DropdownItem>
                    <DropdownItem
                        startContent={isDeleting && <Spinner />}
                        aria-label='delete page'
                        onClick={() => {
                            onClose()
                            if (!userId) {
                                return atoms.showSignupModal.set(true)
                            }
                            del()
                        }}
                    >
                        Delete
                    </DropdownItem>
                </DropdownMenu>
            </Dropdown>

            {folder &&
                createPortal(
                    <>
                        {pageModal}
                        {folderModal}
                    </>,
                    document.body,
                )}
        </>
    )
}

function Node({ node, style, dragHandle, tree }: NodeRendererProps<PagesTree>) {
    const router = useRouter()

    const { pageId, pagesTree } = useRootProps()
    const Icon = node.isLeaf
        ? MdiFile
        : node.isClosed
        ? MaterialSymbolsFolder
        : IcBaselineFolderOpen
    const pageSlug = node.data.slug!
    const siteCursors = useStore(atoms.siteCursors, {
        keys: [pageSlug],
    })

    const cursors = siteCursors[pageSlug]
    const toggle = () => (node.isClosed ? node.open() : node.close())

    const name = path.basename(node.data?.slug)
    let currentPageSlug = getCurrentPageSlug(pagesTree, router.asPath)
    const focused = currentPageSlug === node.data.slug

    const ref = useRef<HTMLDivElement>(null)
    const mergedRef = mergeRefs(dragHandle, ref)
    if (!node?.data?.slug) {
        return null
    }
    const chevron = node.isLeaf ? (
        <div className='w-[13px]'></div>
    ) : node.isClosed ? (
        <ChevronRight className='block opacity-60 shrink-0 w-[13px]' />
    ) : (
        <ChevronDown className='block opacity-60 shrink-0 w-[13px]' />
    )

    return (
        <ContextMenu node={node}>
            <div
                ref={mergedRef}
                onClick={() => {
                    if (!node.isLeaf) {
                        toggle()
                        return
                    }

                    if (!node?.data?.slug) {
                        return
                    }

                    router.push({
                        pathname: withEditorBasePath(
                            router.asPath,
                            node.data.slug,
                        ),
                        query: { mode: router.query.mode },
                    })
                }}
                style={{
                    // ...style,
                    paddingLeft: (Number(style.paddingLeft) || 0) + 4,
                }}
                className={classNames(
                    'flex group mr-6 font-medium relative text-sm hover:opacity-100 items-center h-[28px] !focus:outline-none cursor-pointer appearance-none hover:bg-gray-100/50',
                    'dark:hover:bg-gray-800/50 transition-colors items-center gap-[8px] rounded-md max-w-full',
                    focused && 'bg-gray-100 dark:bg-gray-800 !opacity-100',
                )}
            >
                <div className='flex gap-1 items-center'>
                    {chevron}
                    {Icon && (
                        <Icon className='block opacity-60 shrink-0 h-[16px]' />
                    )}
                </div>
                {node.state.isEditing ? (
                    <RenameForm defaultValue={name} node={node} />
                ) : (
                    <div
                        onDoubleClick={() => {
                            node.edit()
                        }}
                        className={classNames(
                            'truncate block focus:outline-none',
                            // removeQueryStr(router.asPath) === node.data.slug && 'underline'
                        )}
                    >
                        {name}
                    </div>
                )}
                {cursors?.length && (
                    <div className='flex ml-2 gap-1 items-center opacity-50'>
                        {cursors?.map((x) => {
                            const size = 6
                            if (!x) {
                                return null
                            }
                            return (
                                <div
                                    key={x.email}
                                    style={{
                                        backgroundColor: x.color,
                                        width: size,
                                        height: size,
                                        borderRadius: size / 2,
                                    }}
                                    className='mt-px'
                                ></div>
                            )
                        })}
                    </div>
                )}
            </div>
        </ContextMenu>
    )
}

function LinkButton({ name, onClick, ...rest }) {
    return (
        <Tooltip delay={800} closeDelay={0} showArrow content={name}>
            <button
                aria-label={name}
                onClick={(e) => {
                    e.stopPropagation()
                    onClick(e)
                }}
                className='flex opacity-90 transition-all active:opacity-40 items-center justify-center border-0 px-1 py-1 rounded appearance-none'
                {...rest}
            />
        </Tooltip>
    )
}

export const CollapseRight = (props) => {
    return (
        <svg
            viewBox='0 0 5 8'
            fill='currentColor'
            xmlns='http://www.w3.org/2000/svg'
            {...props}
        >
            <path
                d='M0 0.724246C0 0.111374 0.681914 -0.223425 1.13107 0.168926L4.66916 3.25957C5.11028 3.6449 5.11028 4.3551 4.66916 4.74043L1.13107 7.83107C0.681913 8.22342 0 7.88863 0 7.27575V0.724246Z'
                fill='currentColor'
            ></path>
        </svg>
    )
}

export function MdiFile(props) {
    return (
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' {...props}>
            <path
                fill='currentColor'
                d='M13 9V3.5L18.5 9M6 2c-1.11 0-2 .89-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6H6Z'
            ></path>
        </svg>
    )
}

export function MaterialSymbolsFolder(props) {
    return (
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' {...props}>
            <path
                fill='currentColor'
                d='M4 20q-.825 0-1.413-.588T2 18V6q0-.825.588-1.413T4 4h6l2 2h8q.825 0 1.413.588T22 8v10q0 .825-.588 1.413T20 20H4Z'
            ></path>
        </svg>
    )
}

export function IcTwotoneFolderOpen(props) {
    return (
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' {...props}>
            <path fill='currentColor' d='M4 8h16v10H4z' opacity='.3'></path>
            <path
                fill='currentColor'
                d='M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z'
            ></path>
        </svg>
    )
}

export function IcBaselineFolderOpen(props) {
    return (
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' {...props}>
            <path
                fill='currentColor'
                d='M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z'
            ></path>
        </svg>
    )
}

function InfoBlock({ heading, children }) {
    return (
        <div className='flex z-10 relative  flex-col gap-4 rounded-lg dark:bg-gray-900 border px-6 py-6'>
            <div className='flex items-center'>
                <div className='grow font-bold'>{heading}</div>
                <Button
                    variant='ghost'
                    className='-mr-2 border-0'
                    size='sm'
                    isIconOnly
                >
                    <XIcon className='w-4' />
                </Button>
            </div>
            <div className='text-sm '>{children}</div>
        </div>
    )
}
